import { ALERT_MANAGER_EVENT_DISPLAY_TOAST } from '../../../../utils/spds/constants/alert-manager';
import { Arg, Metadata } from '../../../../utils/spds/metadata';
import {
  TOAST_DEFAULT_TIMEOUT,
  TOAST_EXIT_ANIMATION_MS,
} from '../../../../utils/spds/constants/feedback';
import { TrackedObject } from 'tracked-built-ins';
import { get } from '@ember/helper';
import { isTest } from '../../../../utils/is-testing';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { task, timeout as waiter } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { waitForNext, waitForRender } from '../../../../utils/waiters';
import Message from './message.gjs';
import Super from '../../core/base.gjs';
import generateUUID from '../../../../utils/generate-uuid';

const STACK_GAP_PX = 16;

const METADATA = Metadata.define({
  description: `
Container that subscribes to the toast service and renders each broadcast toast as an
individual \`popover="manual"\` element in the browser top layer.

When a modal is open, toasts portal into \`spds/modal\`'s \`topmostOpenModal\` so they stay
hit-testable alongside \`popover\` top-layer rules. After \`in-element\` moves the node,
\`hidePopover\` / \`showPopover\` re-sync the manual popover (Message \`setup\` does not re-run).

The container itself does not paint anything visible; it owns the per-toast bookkeeping:
generating ids, computing per-toast \`offset\` values so toasts stack from the bottom of
the viewport, and propagating \`@animated={{!isTest}}\` so Percy snapshots stay stable.

A single instance of the container should be rendered at the application root. Toasts are
broadcast through the \`spds/toast\` service.

When @local is true (deprecated), this instance does not register with alert-manager, so it
never replaces the global container's handler. Nested copies become inert placeholders until
removed from templates.
  `,
  meta: {
    local: Arg.boolean({
      default: false,
      description:
        'When true, this container does not subscribe to alert-manager so all toasts are handled by the app-level container. Remove nested Spds::Feedback::Toast::Container instances; they are no longer required.',
    }).deprecated({
      id: 'spds.feedback.toast.container.local',
      message:
        '`@local` on Spds::Feedback::Toast::Container is deprecated. When true, the container no longer subscribes to `alert-manager` (only the app-level container should handle toasts). Remove `@local={{true}}` and any nested <Spds::Feedback::Toast::Container> instances.',
    }),
  },
  name: 'Toast Container',
  resources: {
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/128371-toast',
  },
});

export default class SpdsFeedbackToastContainer extends Super {
  #registeredMessages = [];
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-toast-container';
  isTest = isTest();
  toastOffsets = new TrackedObject();

  @tracked lastTarget = null;
  @tracked toasts = [];

  @service alertManager;
  @service fastboot;
  @service('spds/modal') modal;

  get animated() {
    return !(this.isTest || this.targetNeedsUpdate);
  }

  get renderTarget() {
    if (this.fastboot?.isFastBoot) return null;

    return this.modal.topmostOpenModal ?? document.getElementById('ember-testing') ?? document.body;
  }

  get targetNeedsUpdate() {
    return this.lastTarget !== this.renderTarget;
  }

  /**
   * Deprecated `@local={{true}}` flyouts should not register `displayToast` on the shared
   * alert-manager (that would clobber the app-level container). Only the default container
   * subscribes.
   */
  get #isDeprecatedLocalPlaceholder() {
    return this.argOrDefault('local') === true;
  }

  displayToast(toast) {
    this.toasts = [...this.toasts, { id: generateUUID(), ...toast }];

    if (this.running) return;

    this.manageTimer();
  }

  async manageTimer() {
    let toast = this.toasts[0];
    let { timeout = TOAST_DEFAULT_TIMEOUT } = toast;

    if (timeout > 0 && !this.isTest) {
      this.enqueueToastDismissal.perform(toast, timeout);
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.enqueueToastDismissal.cancelAll();
  }

  @(task(function* (toast, t) {
    this.running = true;
    yield waiter(t);
    yield this.autoDismiss(toast);
  }).enqueue())
  enqueueToastDismissal;

  updateToastTarget = modifier(async () => {
    if (!this.targetNeedsUpdate) return;

    await waitForNext();
    await waitForRender();

    // While `lastTarget` is still stale, `animated` is false so Message omits the
    // `animated` class during hide/show (no entry/exit SCSS on reparent).
    this.#refreshToastPopoversAfterReparent();
    this.#computeOffsets();

    // Stacked modals can change `renderTarget` twice before the next paint; a second
    // refresh + frame gives time to match popover top-layer hit-testing.
    await new Promise(resolve => requestAnimationFrame(resolve));
    this.#refreshToastPopoversAfterReparent();
    await new Promise(resolve => requestAnimationFrame(resolve));

    this.lastTarget = this.renderTarget;
  });

  #refreshToastPopoversAfterReparent() {
    for (let message of this.#registeredMessages) {
      if (!message) continue;

      let el = message.baseElement;

      if (message.isDestroyed || message.isDestroying) {
        continue;
      }

      try {
        if (el.matches?.(':popover-open')) {
          try {
            el.hidePopover?.();
          } catch {
            // ignore hide failures while the node is mid-move
          }
        }
        el.showPopover?.();
      } catch {
        // Mid-teardown or invalid popover state while the node is moving.
      }
    }
  }

  #animateDismiss(toast) {
    if (!this.animated) return Promise.resolve();

    let message = this.#registeredMessages.find(m => m.args.toast.id === toast.id);
    let element = message?.baseElement;

    if (!element?.matches?.(':popover-open')) return Promise.resolve();

    toast.dismissing = true;
    this.#computeOffsets();
    element.hidePopover();

    return waiter(TOAST_EXIT_ANIMATION_MS);
  }

  #computeOffsets() {
    let messagesByToastId = new Map(
      this.#registeredMessages.map(message => [message.args.toast.id, message])
    );

    let cumulative = STACK_GAP_PX;

    for (let i = this.toasts.length - 1; i >= 0; i--) {
      let toast = this.toasts[i];

      if (toast.dismissing) continue;

      let message = messagesByToastId.get(toast.id);

      if (!message?.baseElement) continue;

      this.toastOffsets[toast.id] = `${cumulative}px`;
      cumulative += message.baseElement.getBoundingClientRect().height + STACK_GAP_PX;
    }
  }

  #removeToast(toast) {
    let index = this.toasts.findIndex(({ id }) => id === toast.id);
    if (index === -1) return;

    this.toasts.splice(index, 1);
    this.toasts = [...this.toasts];

    if (index === 0 && this.toasts.length) {
      this.manageTimer();
    } else if (this.toasts.length === 0) {
      this.running = false;
    }
  }

  autoDismiss = async toast => {
    await this.#animateDismiss(toast);
    this.#removeToast(toast);
  };

  manualDismiss = toast => {
    toast?.onManualDismissal?.();
    this.autoDismiss(toast);
  };

  registerMessage = message => {
    this.#registeredMessages = [...this.#registeredMessages, message];
  };

  messageReady = () => {
    this.#computeOffsets();
  };

  setup = () => {
    if (this.#isDeprecatedLocalPlaceholder) {
      return;
    }

    this.alertManager.bindEventHandler({
      context: this,
      event: ALERT_MANAGER_EVENT_DISPLAY_TOAST,
      handler: this.displayToast,
    });

    if (!this.fastboot?.isFastBoot) {
      this.lastTarget = document.body;
    }
  };

  unregisterMessage = id => {
    let index = this.#registeredMessages.findIndex(message => message.id === id);
    let toastId;

    if (index > -1) {
      toastId = this.#registeredMessages[index].args.toast.id;
      this.#registeredMessages.splice(index, 1);
    }

    if (toastId) delete this.toastOffsets[toastId];
    this.#computeOffsets();
  };

  <template>
    <span
      class={{this.classes}}
      data-test-spds-feedback-toast-container
      hidden
      {{this.setupComponent}}
      {{this.updateToastTarget}}
      {{this.validateArguments}}
      ...attributes
    ></span>
    {{#each this.toasts key="id" as |toast index|}}
      {{#in-element this.renderTarget insertBefore=null}}
        <Message
          @animated={{this.animated}}
          @index={{index}}
          @offset={{get this.toastOffsets toast.id}}
          @onManualDismiss={{this.manualDismiss}}
          @onReady={{this.messageReady}}
          @registerComponent={{this.registerMessage}}
          @toast={{toast}}
          @unregisterComponent={{this.unregisterMessage}}
        />
      {{/in-element}}
    {{/each}}
  </template>
}

export const TOAST_CONTAINER_METADATA = SpdsFeedbackToastContainer.metadata;
